import ButtonModel, { PageButtonConfig } from '@mm/state/model/ButtonModel';
import { IceBreakerModel } from '@mm/state/model/IceBreakerModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { selectWidgetsByDialogueId } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ExclamationIcon } from '@mm/icons';
import { colors } from '@mm/utils';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import { ReactComponent as FBIcon } from 'assets/icons/live-chat-fb.svg';
import classnames from 'classnames';
import { sortBy } from 'lodash';
import React, {
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { StartBotsDialogueEditor } from '../../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { WidgetAlerts } from '../../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { getNodeName, PathGraphContext } from '../PathGraph/PathGraph';
import './ActivePathSelection.pcss';
import { WidgetLayout } from '../WidgetLayout/WidgetLayout';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import { Tooltip } from '@mm/components';

export type ActivePathType = 'regular' | 'facebook';

type Props = {
  dialogueId?: number;
  widget?: WidgetModel;
  iceBreaker?: IceBreakerModel;
  activeButton?: ButtonModel;
  name?: string;
  isActive?: boolean;
  className?: string;
  chatbotId: number;
  activePathType?: ActivePathType;
  isSmsRelated?: boolean;
  forInstagram?: boolean;
  platforms?: PlatformType[];
  closeExtraActions?: () => void;
  hideClose?: boolean;
  widgetsToExclude?: WidgetTypes[];
  firstWidgetNotAllowedWarning?: WidgetAlerts;
  usedInSlideout?: boolean;
};

const PathTypeIndicator = (props: { activePathType?: ActivePathType }) => {
  const { activePathType } = props;
  switch (activePathType) {
    case 'facebook':
      return (
        <div className="active-path-selection__path-type-indicator-container">
          <div className="active-path-selection__path-type-indicator">
            <FBIcon className="active-path-selection__path-type-indicator-icon" />{' '}
            FB Messenger
            <Tooltip
              title="Messages from this dialogue will be sent on Messenger after user
              logs in"
            >
              <i className="far fa-question-circle active-path-selection__path-type-indicator-help-icon" />
            </Tooltip>
          </div>
        </div>
      );
    case 'regular':
    default:
      return null;
  }
};

const ActivePath = memo((props: Props) => {
  const {
    dialogueId,
    activeButton,
    name,
    isActive,
    widget,
    iceBreaker,
    className,
    chatbotId,
    activePathType,
    isSmsRelated,
    forInstagram,
    platforms,
    closeExtraActions,
    hideClose,
    widgetsToExclude,
    firstWidgetNotAllowedWarning,
    usedInSlideout,
  } = props;

  const loadedAndNotDeletedDialogues = useSelector(
    (state: StoreState) => state.page.entities,
  );

  const { setActiveNode, pathGraph, pathGraphLastUpdate } =
    useContext(PathGraphContext);

  const [activeButtonInit, setActiveButtonInit] = useState(true);
  const activePathRef = useRef<HTMLDivElement>(null);

  const scrollTillActivePath = () => {
    setTimeout(() => {
      if (activePathRef.current) {
        activePathRef.current.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }, 500);
  };

  useEffect(() => {
    if (activeButtonInit) {
      setActiveButtonInit(false);
    } else {
      scrollTillActivePath();
    }
  }, [widget?.buttons.length]);

  let nextLevelDialogueId: number | undefined;

  if (dialogueId) {
    nextLevelDialogueId = dialogueId;
  } else if (activeButton) {
    nextLevelDialogueId = (activeButton.config as PageButtonConfig).pageId;
  } else if (isActive) {
    nextLevelDialogueId = widget?.config?.pageId;
  }

  const nextLevelDialogueWidgets = useSelector((state: StoreState) =>
    sortBy<WidgetModel>(selectWidgetsByDialogueId(nextLevelDialogueId)(state), [
      'sortOrder',
    ]),
  );

  // Note: this is a somewhat hacky solution to the circular reference/infinite loop issue that sometimes
  // happens due to the fact that we get widgets data from Redux.
  // All consumers get widgets data roughly at the same time.
  // The way we process Graph and circular is we add the widgets to the graph, often times with
  // useEffect. We also check for circular references at that point.
  // As a result it occasionally can happen that we start rendering next level before we have
  // valid data on whether it creates a circular reference. This small delay before rendering
  // allows us to get extra time to figure out if we end up with an infinite loop before trying to
  // step into it.
  const [
    isGraphCircularReferenceCalculationDelayOver,
    setIsGraphCircularReferenceCalculationDelayOver,
  ] = useState(false);

  const activeNodeType = activeButton ? 'button' : 'widget';
  const activeNodeId = (activeButton || widget)!.id;

  const hasActiveNode = useMemo(() => {
    const hasEntries = !!widget?.config?.entries;
    const buttons: { id: number }[] = (widget || iceBreaker)!.buttons;

    if (!hasEntries) {
      return (
        !!isActive ||
        !!buttons.find(button =>
          pathGraph?.node(getNodeName(button.id, 'button')),
        )
      );
    }

    const allWidgetButtonsArray =
      widget?.config?.entries?.map(e => e.buttons) || ([[]] as ButtonModel[][]);

    const allWidgetButtons = ([] as ButtonModel[]).concat(
      ...allWidgetButtonsArray,
    );

    return activeButton
      ? !!pathGraph?.node(getNodeName(activeButton.id, 'button')) // Note:  An edge case for yet unsaved button
      : !!allWidgetButtons.find(button =>
          pathGraph?.node(getNodeName(button.id, 'button')),
        );
  }, [isActive, widget, iceBreaker, pathGraphLastUpdate, activeButton]);

  const isCircular = useMemo(() => {
    if (!(hasActiveNode && pathGraph)) return false;

    const currentNodeName = getNodeName(activeNodeId, activeNodeType);

    const nodeOutgoingEdges = pathGraph?.outEdges(currentNodeName);

    if (!nodeOutgoingEdges) return false;
    // Note: it will actually be only 1 edge for both widgets without buttons,
    // and buttons for widgets with buttons. But I'm keeping it more general here.
    const isActiveNodeCircular = nodeOutgoingEdges
      .filter(e => {
        const edgeLabel = pathGraph.edge(e);
        return edgeLabel?.circular || false;
      })
      .some(e => e.v === currentNodeName);

    return isActiveNodeCircular;
  }, [
    pathGraph,
    nextLevelDialogueId,
    hasActiveNode,
    activeNodeId,
    activeNodeType,
    isGraphCircularReferenceCalculationDelayOver,
  ]);

  const isDialogueDeleted = useMemo(
    () => !loadedAndNotDeletedDialogues.some(d => d.id === nextLevelDialogueId),
    [loadedAndNotDeletedDialogues, nextLevelDialogueId],
  );

  const isNextLevelValid = useMemo(() => {
    const res =
      hasActiveNode &&
      !!nextLevelDialogueId &&
      !(isCircular || isDialogueDeleted);

    return res;
  }, [hasActiveNode, nextLevelDialogueId, isCircular, isDialogueDeleted]);

  // Note: we must run it only once (for cases like first load of dialogues/widgets, which seems to be enough).
  // Running it on every change of dialgoues will lead to unmounting/remounting of dialogues below,
  // leading to them losing their state.
  useEffect(() => {
    setIsGraphCircularReferenceCalculationDelayOver(false);
    setTimeout(
      () => setIsGraphCircularReferenceCalculationDelayOver(true),
      300,
    );
  }, []);

  if (!nextLevelDialogueId || isDialogueDeleted) return null;

  return (
    <div
      ref={activePathRef}
      className={classnames('active-path-selection', className)}
    >
      {hideClose ? (
        <div className="active-path-selection__top-wrapper-left">
          <div className="active-path-selection__path-line" />
        </div>
      ) : (
        <div className="active-path-selection__top-wrapper">
          <div className="active-path-selection__buttons">
            <div
              className="active-path-selection__active-path-close-button"
              onClick={() => {
                setActiveNode?.({
                  id: activeNodeId,
                  type: activeNodeType,
                  action: 'close',
                });
                closeExtraActions?.();
              }}
            >
              <CloseIcon className="active-path-selection__active-path-close-button-icon" />

              <span>Close Path</span>
            </div>

            <div className="active-path-selection__active-path-area">
              <div className="active-path-selection__active-path-name">
                <div className="active-path-selection__active-path-close-button-text">
                  {activeButton?.name || name}
                </div>
              </div>
              <div className="active-path-selection__path-line" />
            </div>
          </div>
        </div>
      )}

      {isCircular && (
        <div className="active-path-selection__alert-same-dialog">
          <ExclamationIcon
            color={colors.orange}
            className="active-path-selection__alert-same-dialog-icon"
          />
          <span>This dialog points to itself.</span>
          <span>
            <span
              onClick={() => {
                const element =
                  document.getElementsByClassName('c-scroll-area')[0];
                element.scroll({
                  top: 0,
                  left: 0,
                  behavior: 'smooth',
                });
              }}
              className="active-path-selection__alert-go-up"
            >
              Go up
            </span>
            to the beginning of the dialog or assign another dialog to continue
          </span>
        </div>
      )}

      {isGraphCircularReferenceCalculationDelayOver && isNextLevelValid && (
        <div className="active-path-selection__active-button-path">
          <div className="active-path-selection__active-button-path-top">
            <div className="active-path-selection__active-button-path-ball-top active-path-selection__active-button-path-ball-left" />
            <PathTypeIndicator activePathType={activePathType} />
            <div className="active-path-selection__active-button-path-ball-top active-path-selection__active-button-path-ball-right" />
          </div>

          <StartBotsDialogueEditor
            isPath={true} // helps us not to re-fetch already available data on each path render, consider re-designing this
            chatbotId={chatbotId}
            widgets={nextLevelDialogueWidgets}
            dialogueId={nextLevelDialogueId}
            isSmsRelated={isSmsRelated}
            forInstagram={forInstagram}
            platforms={platforms}
            specificityId={Math.floor(Math.random() * 1000000)}
            widgetsToExclude={widgetsToExclude}
            firstWidgetNotAllowedWarning={firstWidgetNotAllowedWarning}
            usedInSlideout={usedInSlideout}
          />
        </div>
      )}
    </div>
  );
});

export const ActivePathSelectionFactory = memo((props: Props) => {
  const { widget, activeButton } = props;
  const { getActiveButtonFromWidget } = useContext(PathGraphContext);

  const button = useMemo(() => {
    return activeButton || getActiveButtonFromWidget?.(widget);
  }, [
    activeButton,
    widget?.buttons.length,
    widget?.config.entries?.length,
    getActiveButtonFromWidget,
  ]);

  if (!widget) {
    return <ActivePath {...props} />;
  }

  return button || props.isActive ? (
    <WidgetLayout.PathPortal widget={widget}>
      <ActivePath {...props} activeButton={button} />
    </WidgetLayout.PathPortal>
  ) : (
    <></>
  );
});

export const ActivePathSelection = ActivePath;
